import { Button, TextButton } from '@creditornot/cb-button';
import { ArrowLeft } from '@creditornot/cb-icons';
import { cbSemanticCssVars, Typography as T } from '@creditornot/cb-ingredients';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FORM_ERROR } from 'final-form';
import { pipe } from 'ramda';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { NumberParam, useQueryParam } from 'use-query-params';

import { getProductInformationSettings } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category } from 'apis/restaurant-api/merchant-admin/menu2/types';
import {
  createRestockingRule,
  getRestockingRules,
  editRestockingRule,
  deleteRestockingRule,
} from 'apis/restocking-rules/api';
import { RestockingRule } from 'apis/restocking-rules/types';
import { APIErrorNotification, ConfirmModal } from 'common/components';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { isDefined } from 'common/utils';

import { RestockingRuleFormValues } from './RestockingRuleFields';
import RestockingRuleModal from './RestockingRuleModal';
import RestockingRulesTable from './RestockingRulesTable';
import { buildRestockingRulePayload, buildRestockingRuleFormValues } from './utils';

type Props = {
  menuId: string;
  venueSlug: string;
  timezone: string;
  venueId: string;
  categories: Category[];
  primaryLanguage: Language;
};

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  color: ${cbSemanticCssVars.textPrimary};
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const Header = styled.div`
  padding: 24px 8px 24px 24px;
  display: flex;
  justify-content: space-between;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const Navigation = styled.div`
  padding-block-start: 24px;
  padding-inline-start: 24px;
  padding-inline-end: 24px;
`;

const Heading = styled(T.Heading4)`
  margin-block-start: 8px;
`;

const StyledAPIErrorNotification = styled(APIErrorNotification)`
  margin: 24px;
`;

const currentDate = new Date();

const INITIAL_CREATE_FORM_VALUES: RestockingRuleFormValues = {
  ruleName: '',
  categoryIds: [],
  productFamilyIds: [],
  type: 'AUTO_RESTOCKING',
  frequency: 'DAILY',
  weekdays: [],
  timeInMinutes: 8 * 60,
  applyAfterDays: 2,
  startDate: currentDate,
};

type Action =
  | { type: 'CREATE_RULE' }
  | { type: 'EDIT_RULE'; rule: RestockingRule }
  | { type: 'DELETE_RULE'; id: string };

const PageSizeParam = {
  encode: NumberParam.encode,
  decode: pipe(NumberParam.decode, (pageSize: number) => ([5, 10, 20].includes(pageSize) ? pageSize : 20)),
};

const useRestockingRulesQuery = (venueId: string) => {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [pageSize, setPageSize] = useQueryParam('pageSize', PageSizeParam);
  const { data, isLoading, error, refetch } = useQuery(
    ['restocking-rules', venueId, cursor, pageSize],
    () => getRestockingRules(venueId, { cursor, limit: pageSize }),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  const handleSetPageSize = useCallback(
    (size: number) => {
      setCursor(undefined);
      setPageSize(size);
    },
    [setPageSize],
  );

  return {
    data,
    isLoading,
    error,
    refetch,
    setCursor,
    pageSize,
    setPageSize: handleSetPageSize,
  };
};

const RestockingRulesPage = ({ menuId, venueSlug, venueId, categories, primaryLanguage, timezone }: Props) => {
  const history = useHistory();
  const intl = useIntl();
  const [action, setAction] = useState<Action | null>(null);
  const { data: productInfoSettings, error: productInfoSettingsError } = useQuery(
    [`product-information-settings/${menuId}`],
    () => getProductInformationSettings(menuId),
    { refetchOnMount: false, refetchOnWindowFocus: false },
  );
  const {
    data: restockingRulesData,
    isLoading: isLoadingRestockingRulesData,
    error: restockingRulesDataError,
    refetch: refetchRestockingRulesData,
    setCursor,
    pageSize,
    setPageSize,
  } = useRestockingRulesQuery(venueId);

  const handleBackClick = useCallback(() => {
    history.push({
      pathname: `/venue/${venueSlug}/menu-editor`,
    });
  }, [history, venueSlug]);

  const closeAction = useCallback(() => {
    setAction(null);
  }, [setAction]);

  const handleAddRuleClick = useCallback(() => {
    setAction({
      type: 'CREATE_RULE',
    });
  }, []);

  const handleEditRuleClick = useCallback((rule: RestockingRule) => setAction({ type: 'EDIT_RULE', rule }), []);

  const handleDeleteRuleClick = useCallback((id: string) => setAction({ type: 'DELETE_RULE', id }), []);

  const initialFormValues = useMemo(
    () => (action?.type === 'EDIT_RULE' ? buildRestockingRuleFormValues(action.rule) : INITIAL_CREATE_FORM_VALUES),
    [action],
  );

  const handleSubmit = useCallback(
    async (values: RestockingRuleFormValues) => {
      if (action?.type !== 'CREATE_RULE' && action?.type !== 'EDIT_RULE') {
        throw new Error('Unknown action! This should not happen.');
      }

      try {
        const payload = buildRestockingRulePayload(values, categories, menuId, timezone);

        if (action.type === 'CREATE_RULE') {
          await createRestockingRule(venueId, payload);
        }

        if (action.type === 'EDIT_RULE') {
          await editRestockingRule(venueId, action.rule.id, payload);
        }

        await refetchRestockingRulesData();
        setAction(null);
        return;
      } catch (error) {
        return {
          [FORM_ERROR]: error,
        };
      }
    },
    [action, categories, menuId, refetchRestockingRulesData, timezone, venueId],
  );

  const restockingRuleModalTitle = useMemo(
    () =>
      [
        action?.type === 'CREATE_RULE'
          ? intl.formatMessage({ id: 'menu.restockingRules.ruleForm.addRuleTitle' })
          : undefined,
        action?.type === 'EDIT_RULE'
          ? intl.formatMessage({ id: 'menu.restockingRules.ruleForm.editRuleTitle' })
          : undefined,
        '',
      ].filter(isDefined)[0],
    [action, intl],
  );

  const { mutate: deleteRule, isLoading: deletingRule } = useMutation(
    async () => {
      if (action?.type !== 'DELETE_RULE') {
        throw new Error('Unknown action');
      }
      await deleteRestockingRule(venueId, action.id);
    },
    {
      onSuccess: async () => {
        await refetchRestockingRulesData();
        setAction(null);
      },
      onError: (error: unknown) => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  return (
    <>
      <Root>
        <Navigation>
          <TextButton onClick={handleBackClick} variant="lightGrey" icon={<ArrowLeft />}>
            {intl.formatMessage({ id: 'menu.restockingRules.back' })}
          </TextButton>
        </Navigation>
        <Header>
          <Heading>{intl.formatMessage({ id: 'menu.restockingRules.heading' })}</Heading>
          <Button onClick={handleAddRuleClick} size="small" variant="blue">
            {intl.formatMessage({ id: 'menu.restockingRules.addRuleButton' })}
          </Button>
        </Header>
        {!!restockingRulesDataError && <StyledAPIErrorNotification error={restockingRulesDataError} />}
        {!!productInfoSettingsError && <StyledAPIErrorNotification error={productInfoSettingsError} />}
        <RestockingRulesTable
          data={restockingRulesData}
          loading={isLoadingRestockingRulesData}
          onEditClick={handleEditRuleClick}
          onDeleteClick={handleDeleteRuleClick}
          onNextPageClick={setCursor}
          onPrevPageClick={setCursor}
          onPageSizeChange={setPageSize}
          pageSize={pageSize}
          language={primaryLanguage}
          productFamilies={productInfoSettings?.product_hierarchy_levels ?? []}
          categories={categories}
        />
      </Root>

      <RestockingRuleModal
        title={restockingRuleModalTitle}
        initialFormValues={initialFormValues}
        venueId={venueId}
        onSubmit={handleSubmit}
        isOpen={action?.type === 'CREATE_RULE' || action?.type === 'EDIT_RULE'}
        onClose={closeAction}
        categories={categories}
        language={primaryLanguage}
        productFamilies={productInfoSettings?.product_hierarchy_levels ?? []}
      />

      <ConfirmModal
        header={intl.formatMessage({ id: 'menu.restockingRules.deleteRule.title' })}
        description={intl.formatMessage({ id: 'menu.restockingRules.deleteRule.description' })}
        onCancel={closeAction}
        onConfirm={deleteRule}
        loading={deletingRule}
        open={action?.type === 'DELETE_RULE'}
      />
    </>
  );
};

export default RestockingRulesPage;
